import { Button, ButtonType } from '@UI/components/controls/Button/Button.tsx'
import { ArrowDownUpDown, ArrowDownUpUp } from '@UI/components/icons/ArrowDownUp.tsx'
import { ColorNames } from '@UI/theme/generated/ColorNames.ts'
import { Body_16_Coral_Strong_emphasis_16 } from '@UI/typography/generated/typography.tsx'
import { FilterCalendar } from '@src/CommonComponents/Calendar/FilterCalendar.tsx'
import { ManyDaysCards, OneDayCard } from '@src/CommonComponents/EventsHistoryElement/OneDayCard/OneDayCard.tsx'
import { Stack } from '@src/CommonComponents/Stack/Stack.tsx'
import { WorkDayInfo } from '@src/CommonComponents/WorkDayInfo/WorkDayInfo.tsx'
import { ButtonVar, DataNotFoundMessage, ErrorLoadingMessage, PeriodLabel } from '@src/Pages/Views/Common.tsx'
import { useEventsStore } from '@src/Store/eventsStore.ts'
import { useWorkDaysStore } from '@src/Store/workDaysStore.ts'
import { Gt } from '@src/i18n/Gt.tsx'
import { formatDate } from '@src/utils/formatDate.ts'
import cn from 'classnames'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styles from './attendanceHistoryPage.module.less'

export const SortingButton = ({
    sortDirection,
    setSortDirection
}: {
    sortDirection: 'ASC' | 'DESC'
    setSortDirection: (val: 'ASC' | 'DESC') => void
}) => {
    return (
        <Button
            type={ButtonType.clickableIcon}
            color={ColorNames.OBSIDIAN}
            className={styles.sortingButton}
            icon={sortDirection === 'DESC' ? <ArrowDownUpUp direction={'DESC'} /> : <ArrowDownUpDown direction={'ASC'} />}
            onClick={() => setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')}
        />
    )
}

const FiltersBlock = ({ setSelectedFilter }: { setSelectedFilter: (e: ButtonVar) => void }) => {
    const navigate = useNavigate()
    const [params] = useSearchParams()
    const currentWorkDay = useWorkDaysStore(x => x.currentWorkDay)
    const groupEvents = useEventsStore(x => x.groupedEvents)

    const [selectedButton, setSelectedButton] = useState<ButtonVar>(ButtonVar.today)

    useEffect(() => {
        setSelectedFilter(selectedButton)
    }, [selectedButton])

    useEffect(() => {
        const p = new URLSearchParams(params)
        p.delete('date')
        p.delete('date_range_after')
        p.delete('date_range_before')
        p.delete('record_range_after')
        p.delete('record_range_before')
        switch (selectedButton) {
            case ButtonVar.today: {
                p.set('date', formatDate(new Date().toISOString()))
                p.set('record_range_after', formatDate(new Date().toISOString()))
                break
            }
            case ButtonVar.thisWeek: {
                const dt = new Date()
                dt.setDate(dt.getDate() - dt.getDay())
                p.set('date_range_after', formatDate(dt.toISOString()))
                p.set('record_range_after', formatDate(dt.toISOString()))
                break
            }
            case ButtonVar.thisMonth: {
                const dt = new Date()
                dt.setDate(1)
                p.set('date_range_after', formatDate(dt.toISOString()))
                p.set('record_range_after', formatDate(dt.toISOString()))
                break
            }
        }
        navigate(`?${p.toString()}`, { replace: true })
        // setParams(p)
    }, [selectedButton])

    return (
        <Stack direction={'column'} gap={16} className={styles.filtersBlock}>
            <div className={styles.buttonsGroup}>
                <Button
                    type={ButtonType.third}
                    onClick={() => setSelectedButton(ButtonVar.today)}
                    className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.today })}
                >
                    <Gt>Today</Gt>
                </Button>
                <Button
                    type={ButtonType.third}
                    onClick={() => setSelectedButton(ButtonVar.thisWeek)}
                    className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.thisWeek })}
                >
                    <Gt>This week</Gt>
                </Button>
                <Button
                    type={ButtonType.third}
                    onClick={() => setSelectedButton(ButtonVar.thisMonth)}
                    className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.thisMonth })}
                >
                    <Gt>This month</Gt>
                </Button>
                <Button
                    type={ButtonType.third}
                    onClick={() => setSelectedButton(ButtonVar.manual)}
                    className={cn(styles.button, { [styles.activeButton]: selectedButton === ButtonVar.manual })}
                >
                    <Gt>Date specification</Gt>
                </Button>
            </div>
            {selectedButton === ButtonVar.manual && <FilterCalendar />}
            <WorkDayInfo workDay={currentWorkDay} events={groupEvents} showGreetings={false} />
        </Stack>
    )
}

export const EmployeeView = () => {
    const currentWorkDay = useWorkDaysStore(x => x.currentWorkDay)
    const workdays = useWorkDaysStore(x => x.workdays)
    const eventsGroups = useEventsStore(x => x.groupedEvents)
    const eventsLoadError = useEventsStore(x => x.error)

    const [sortingRule, setSortingRule] = useState<'ASC' | 'DESC'>('DESC')
    const [selectedFilter, setSelectedFilter] = useState<ButtonVar>(ButtonVar.today)

    return (
        <Stack className={styles.root} direction={'column'} gap={16}>
            <FiltersBlock setSelectedFilter={setSelectedFilter} />
            <Stack direction={'column'} gap={8} className={styles.content}>
                <Stack alignItems={'center'} justifyContent={'space-between'}>
                    <Stack alignItems={'center'} className={styles.dateDiapazon}>
                        <Body_16_Coral_Strong_emphasis_16>
                            <PeriodLabel period={selectedFilter} />
                        </Body_16_Coral_Strong_emphasis_16>
                    </Stack>
                    <SortingButton sortDirection={sortingRule} setSortDirection={setSortingRule} />
                </Stack>

                {selectedFilter === ButtonVar.today ? (
                    <OneDayCard
                        eventsGroups={eventsGroups}
                        workday={currentWorkDay}
                        date={currentWorkDay?.date ?? new Date().toString()}
                        overflowEvents={true}
                    />
                ) : (
                    <div className={styles.eventsBlock}>
                        <ManyDaysCards
                            eventsGroups={eventsGroups}
                            workdays={workdays}
                            date={currentWorkDay?.date!}
                            overflowEvents={true}
                            sortingRule={sortingRule}
                        />
                    </div>
                )}

                {eventsGroups?.length === 0 && <DataNotFoundMessage />}
                {eventsLoadError && <ErrorLoadingMessage />}
            </Stack>
        </Stack>
    )
}
